import { useState, useEffect } from 'react';
import { Card, Button, InputNumber, Input, message, Spin, Tag, Table, Statistic, Row, Col, Tabs, Descriptions, Space, Modal, Alert } from 'antd';
import { DollarOutlined, PlusOutlined, HistoryOutlined, EnvironmentOutlined, RobotOutlined, ThunderboltOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import ApiService from '../services/api';

const { TextArea } = Input;

const Pricing = () => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [fuelPrices, setFuelPrices] = useState({ current_price: null, history: [] });
  const [priceCards, setPriceCards] = useState({ active_card: null, cards: [] });
  const [newFuelPrice, setNewFuelPrice] = useState({ price_per_liter: '', notes: '' });
  const [generatorPrompt, setGeneratorPrompt] = useState('Generate a competitive price card for tipper trucks based on current fuel prices and historical trip data. Optimize for profitability while remaining competitive in the Harare market.');
  const [generatedCard, setGeneratedCard] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [previewModal, setPreviewModal] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [fuelData, cardsData] = await Promise.all([
        ApiService.getFuelPrices(),
        ApiService.getPriceCards()
      ]);
      setFuelPrices(fuelData);
      setPriceCards(cardsData);
    } catch (error) {
      console.error('Error loading pricing data:', error);
      message.error('Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddFuelPrice = async () => {
    if (!newFuelPrice.price_per_liter) { message.warning('Enter fuel price'); return; }
    setLoading(true);
    try {
      await ApiService.addFuelPrice(newFuelPrice);
      message.success('Fuel price updated successfully');
      setNewFuelPrice({ price_per_liter: '', notes: '' });
      loadData();
    } catch (error) { message.error('Failed to add fuel price'); } finally { setLoading(false); }
  };

  const handleGeneratePriceCard = async () => {
    setGenerating(true);
    try {
      const result = await ApiService.generatePriceCard({
        prompt: generatorPrompt,
        current_fuel_price: fuelPrices.current_price?.price_per_liter || 2.05
      });
      setGeneratedCard(result.generated_card);
      setAnalysisData(result.analysis);
      setPreviewModal(true);
      message.success('Price card generated! Review and save if satisfied.');
    } catch (error) {
      console.error('Generation error:', error);
      message.error('Failed to generate price card');
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveGeneratedCard = async () => {
    if (!generatedCard) return;
    setLoading(true);
    try {
      await ApiService.saveGeneratedPriceCard(generatedCard);
      message.success('Price card saved and activated!');
      setPreviewModal(false);
      setGeneratedCard(null);
      loadData();
    } catch (error) {
      message.error('Failed to save price card');
    } finally {
      setLoading(false);
    }
  };

  const fuelHistoryColumns = [
    { title: 'Date', dataIndex: 'effective_date', key: 'date', width: 120 },
    { title: 'Price/L', dataIndex: 'price_per_liter', key: 'price', render: v => `$${v?.toFixed(2)}`, width: 100 },
    { title: 'Notes', dataIndex: 'notes', key: 'notes' },
    { title: 'Status', dataIndex: 'is_active', key: 'status', render: v => v ? <Tag color="green">Active</Tag> : <Tag>Historical</Tag>, width: 100 }
  ];

  const destinationsColumns = [
    { title: 'Destination', dataIndex: 'name', key: 'name' },
    { title: 'Zone', dataIndex: 'zone', key: 'zone', render: v => <Tag color="blue">Zone {v}</Tag> },
    { title: 'Distance', dataIndex: 'distance_km', key: 'distance', render: v => `${v} km` },
    { title: 'Base Price', dataIndex: 'base_price', key: 'price', render: v => `$${v}` }
  ];

  if (loading && !fuelPrices.current_price) return <div style={{ padding: 40, textAlign: 'center' }}><Spin size="large" /></div>;

  const activeCard = priceCards.active_card;
  const destinations = activeCard?.destinations || [];

  return (
    <div>
      <h2 style={{ marginBottom: 24 }}>Pricing Management</h2>
      
      <Tabs defaultActiveKey="generator" items={[
        {
          key: 'generator',
          label: <span><RobotOutlined /> AI Price Card Generator</span>,
          children: (
            <Row gutter={24}>
              <Col span={24}>
                <Card title={<span><ThunderboltOutlined /> Generate Price Card with AI</span>}>
                  <Alert
                    message="AI-Powered Pricing"
                    description="The AI analyzes your historical trip data, fuel price trends, and market conditions to generate an optimized price card. Enter your requirements below and click Generate."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                  
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 8 }}>
                      Pricing Instructions / Requirements:
                    </label>
                    <TextArea
                      rows={4}
                      value={generatorPrompt}
                      onChange={e => setGeneratorPrompt(e.target.value)}
                      placeholder="E.g., Generate competitive prices for tipper trucks. Consider the recent fuel price increase. Maintain 15% profit margin. Focus on Harare suburbs..."
                    />
                  </div>
                  
                  <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={8}>
                      <Card size="small">
                        <Statistic 
                          title="Current Fuel Price" 
                          value={fuelPrices.current_price?.price_per_liter || 'Not set'} 
                          prefix="$" 
                          suffix="/L"
                          valueStyle={{ color: '#1890ff' }}
                        />
                      </Card>
                    </Col>
                    <Col span={8}>
                      <Card size="small">
                        <Statistic 
                          title="Active Price Card" 
                          value={activeCard?.name || 'None'} 
                          valueStyle={{ fontSize: 14 }}
                        />
                      </Card>
                    </Col>
                    <Col span={8}>
                      <Card size="small">
                        <Statistic 
                          title="Historical Data" 
                          value={priceCards.cards?.length || 0} 
                          suffix=" cards"
                        />
                      </Card>
                    </Col>
                  </Row>
                  
                  <Button 
                    type="primary" 
                    size="large" 
                    icon={<RobotOutlined />} 
                    onClick={handleGeneratePriceCard}
                    loading={generating}
                    block
                  >
                    {generating ? 'Analyzing Data & Generating...' : 'Generate AI Price Card'}
                  </Button>
                </Card>
              </Col>
            </Row>
          )
        },
        {
          key: 'fuel',
          label: <span><DollarOutlined /> Fuel Prices</span>,
          children: (
            <Row gutter={24}>
              <Col span={8}>
                <Card title="Current Fuel Price">
                  {fuelPrices.current_price ? (
                    <Statistic 
                      value={fuelPrices.current_price.price_per_liter} 
                      prefix="$" 
                      suffix="/L" 
                      precision={2} 
                      valueStyle={{ color: '#1890ff', fontSize: '36px' }} 
                    />
                  ) : <div style={{ color: '#999', fontSize: 18 }}>No fuel price set</div>}
                  {fuelPrices.current_price && (
                    <div style={{ marginTop: 8, color: '#666' }}>
                      Effective: {fuelPrices.current_price.effective_date}
                    </div>
                  )}
                  <div style={{ marginTop: 24 }}>
                    <Space.Compact style={{ width: '100%' }}>
                      <InputNumber 
                        placeholder="New price/L" 
                        value={newFuelPrice.price_per_liter} 
                        onChange={v => setNewFuelPrice({ ...newFuelPrice, price_per_liter: v })} 
                        style={{ width: '40%' }} 
                        min={0} 
                        step={0.01} 
                        prefix="$" 
                      />
                      <Input 
                        placeholder="Notes (optional)" 
                        value={newFuelPrice.notes} 
                        onChange={e => setNewFuelPrice({ ...newFuelPrice, notes: e.target.value })} 
                        style={{ width: '40%' }} 
                      />
                      <Button type="primary" icon={<PlusOutlined />} onClick={handleAddFuelPrice} loading={loading}>
                        Update
                      </Button>
                    </Space.Compact>
                  </div>
                </Card>
              </Col>
              <Col span={16}>
                <Card title={<span><HistoryOutlined /> Price History</span>}>
                  <Table 
                    dataSource={fuelPrices.history} 
                    columns={fuelHistoryColumns} 
                    rowKey="fuel_price_id"
                    pagination={{ pageSize: 10 }}
                    size="small"
                  />
                </Card>
              </Col>
            </Row>
          )
        },
        {
          key: 'pricecard',
          label: <span><EnvironmentOutlined /> Active Price Card</span>,
          children: (
            <Row gutter={24}>
              <Col span={24}>
                {activeCard ? (
                  <Card title={`Active Price Card: ${activeCard.name}`}>
                    <Descriptions bordered column={4} size="small" style={{ marginBottom: 24 }}>
                      <Descriptions.Item label="Card ID">{activeCard.card_id}</Descriptions.Item>
                      <Descriptions.Item label="Created">{activeCard.created_at?.split('T')[0]}</Descriptions.Item>
                      <Descriptions.Item label="Fuel Reference">${activeCard.fuel_price_reference}/L</Descriptions.Item>
                      <Descriptions.Item label="Status"><Tag color="green">Active</Tag></Descriptions.Item>
                    </Descriptions>
                    
                    {activeCard.zones && (
                      <>
                        <h4>Zone Pricing</h4>
                        <Row gutter={16} style={{ marginBottom: 24 }}>
                          {activeCard.zones.map((zone, i) => (
                            <Col span={6} key={i}>
                              <Card size="small" style={{ textAlign: 'center', backgroundColor: '#f6ffed' }}>
                                <div style={{ fontWeight: 'bold', fontSize: 12 }}>{zone.zone || `Zone ${i+1}`}</div>
                                <div style={{ fontSize: 11, color: '#666' }}>{zone.distance_range}</div>
                                <div style={{ fontSize: 18, color: '#52c41a' }}>${zone.min_price}-${zone.max_price}</div>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      </>
                    )}

                    {activeCard.quick_reference && (
                      <>
                        <h4>Quick Reference</h4>
                        <Table 
                          dataSource={activeCard.quick_reference} 
                          columns={[
                            { title: 'Distance', dataIndex: 'distance', key: 'distance' },
                            { title: 'Price Range', dataIndex: 'price_range', key: 'price' }
                          ]}
                          rowKey="distance"
                          pagination={false}
                          size="small"
                          style={{ marginBottom: 24 }}
                        />
                      </>
                    )}

                    {activeCard.surcharges && (
                      <>
                        <h4>Surcharges</h4>
                        <Row gutter={16} style={{ marginBottom: 24 }}>
                          {Object.entries(activeCard.surcharges).map(([key, value]) => (
                            <Col span={4} key={key}>
                              <Card size="small" style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: 11, textTransform: 'capitalize' }}>{value.name || key.replace(/_/g, ' ')}</div>
                                <div style={{ fontSize: 16, color: '#fa8c16' }}>
                                  {value.percentage ? `+${value.percentage}%` : value.hourly_rate ? `$${value.hourly_rate}/hr` : ''}
                                </div>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      </>
                    )}

                    {destinations.length > 0 && (
                      <>
                        <h4><EnvironmentOutlined /> Destinations ({destinations.length})</h4>
                        <Table 
                          dataSource={destinations} 
                          columns={destinationsColumns} 
                          rowKey="name"
                          pagination={{ pageSize: 10 }}
                          size="small"
                        />
                      </>
                    )}
                  </Card>
                ) : (
                  <Card>
                    <div style={{ textAlign: 'center', padding: 40 }}>
                      <RobotOutlined style={{ fontSize: 48, color: '#999', marginBottom: 16 }} />
                      <p>No active price card. Use the AI Generator to create one!</p>
                    </div>
                  </Card>
                )}
              </Col>
            </Row>
          )
        }
      ]} />

      <Modal
        title={<span><RobotOutlined /> Generated Price Card Preview</span>}
        open={previewModal}
        onCancel={() => setPreviewModal(false)}
        width={900}
        footer={[
          <Button key="regenerate" icon={<ReloadOutlined />} onClick={() => { setPreviewModal(false); handleGeneratePriceCard(); }}>
            Regenerate
          </Button>,
          <Button key="cancel" onClick={() => setPreviewModal(false)}>
            Cancel
          </Button>,
          <Button key="save" type="primary" icon={<SaveOutlined />} onClick={handleSaveGeneratedCard} loading={loading}>
            Save & Activate
          </Button>
        ]}
      >
        {generatedCard && (
          <div>
            {analysisData && (
              <Alert
                message={`Analysis: ${analysisData.total_trips_analyzed} trips analyzed`}
                description={`Fuel adjustment: ${analysisData.fuel_adjustment_factor?.toFixed(2)}x | Confidence: ${generatedCard.ai_confidence}`}
                type="success"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            
            <h4>Quick Reference Table</h4>
            <Table 
              dataSource={generatedCard.quick_reference} 
              columns={[
                { title: 'Distance', dataIndex: 'distance', key: 'distance' },
                { title: 'Price Range', dataIndex: 'price_range', key: 'price' }
              ]}
              rowKey="distance"
              pagination={false}
              size="small"
              style={{ marginBottom: 16 }}
            />
            
            <h4>Zone Details</h4>
            <Row gutter={8} style={{ marginBottom: 16 }}>
              {generatedCard.zones?.map((zone, i) => (
                <Col span={6} key={i}>
                  <Card size="small" style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 11, fontWeight: 'bold' }}>{zone.zone}</div>
                    <div style={{ fontSize: 10, color: '#666' }}>{zone.distance_range}</div>
                    <div style={{ fontSize: 16, color: '#1890ff' }}>${zone.min_price}-${zone.max_price}</div>
                    <div style={{ fontSize: 10, color: '#999' }}>${zone.avg_price_per_km}/km avg</div>
                  </Card>
                </Col>
              ))}
            </Row>
            
            {generatedCard.destinations?.length > 0 && (
              <>
                <h4>Destinations ({generatedCard.destinations.length})</h4>
                <Table 
                  dataSource={generatedCard.destinations.slice(0, 10)} 
                  columns={[
                    { title: 'Location', dataIndex: 'name', key: 'name' },
                    { title: 'Zone', dataIndex: 'zone', key: 'zone', render: v => <Tag>Zone {v}</Tag> },
                    { title: 'Distance', dataIndex: 'distance_km', key: 'dist', render: v => `${v} km` },
                    { title: 'Price', dataIndex: 'base_price', key: 'price', render: v => `$${v}` },
                    { title: 'Trips', dataIndex: 'trip_count', key: 'trips' }
                  ]}
                  rowKey="name"
                  pagination={false}
                  size="small"
                />
                {generatedCard.destinations.length > 10 && (
                  <div style={{ textAlign: 'center', color: '#999', marginTop: 8 }}>
                    ...and {generatedCard.destinations.length - 10} more destinations
                  </div>
                )}
              </>
            )}
            
            <h4 style={{ marginTop: 16 }}>Surcharges</h4>
            <Row gutter={8}>
              {Object.entries(generatedCard.surcharges || {}).map(([key, value]) => (
                <Col span={4} key={key}>
                  <Card size="small" style={{ textAlign: 'center', fontSize: 10 }}>
                    <div>{value.name}</div>
                    <div style={{ color: '#fa8c16', fontWeight: 'bold' }}>
                      {value.percentage ? `+${value.percentage}%` : `$${value.hourly_rate}/hr`}
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Pricing;
